import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';

/**
 * 강사용 출석 등록 페이지
 * 강사가 강의와 학생을 직접 선택하여 출석을 등록할 수 있는 페이지
 */
const AttendanceRegister = () => {
  const { authAxios, user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();
  const selectedClassId = watch('classId');

  // 강의 목록 가져오기
  const fetchClasses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // API 호출
      const response = await authAxios.get('/classes');
      setClasses(response.data);
      setIsLoading(false);
    } catch (err) {
      setError('강의 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      console.error('Classes list error:', err);
    }
  }, [authAxios]);

  // 교육생 목록 가져오기
  const fetchStudents = useCallback(async (classId) => {
    if (!classId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // API 호출
      const response = await authAxios.get(`/classes/${classId}/students`);
      setStudents(response.data);
      setIsLoading(false);
    } catch (err) {
      setError('교육생 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      console.error('Students list error:', err);
    }
  }, [authAxios]);

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // 강의 선택 시 해당 강의의 교육생 목록 불러오기
  useEffect(() => {
    if (selectedClassId) {
      fetchStudents(selectedClassId);
      // 선택된 강의 정보 저장
      const selectedClass = classes.find(c => c.id === selectedClassId);
      setSelectedClass(selectedClass);
    }
  }, [selectedClassId, classes, fetchStudents]);

  // 출석 등록 핸들러
  const onSubmitAttendance = async (data) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // 출석 데이터 준비
      const attendanceData = {
        classId: data.classId,
        studentId: data.studentId,
        status: data.status,
        registeredBy: user.id, // 출석을 등록하는 사용자 ID
        date: new Date().toISOString().split('T')[0], // 현재 날짜
        note: data.note || ''
      };
      
      // API 호출
      const response = await authAxios.post('/attendance', attendanceData);
      
      // 성공 메시지 표시
      setSuccessMessage('출석이 성공적으로 등록되었습니다.');
      
      // 폼 초기화
      reset();
      
      // 3초 후 성공 메시지 초기화
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      setIsSubmitting(false);
    } catch (err) {
      setError('출석 등록 중 오류가 발생했습니다.');
      console.error('Attendance registration error:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">출석 등록</h1>
        <p className="mt-1 text-sm text-gray-500">
          강의별 교육생 출석을 등록할 수 있습니다.
        </p>
      </div>

      {/* 출석 등록 폼 */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit(onSubmitAttendance)} className="space-y-4">
            <div>
              <label htmlFor="classId" className="block text-sm font-medium text-gray-700">
                강의 선택
              </label>
              <select
                id="classId"
                {...register('classId', { required: '강의를 선택해주세요.' })}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="">강의를 선택하세요</option>
                {classes.map((classItem) => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name} - {classItem.schedule} ({classItem.room})
                  </option>
                ))}
              </select>
              {errors.classId && (
                <p className="mt-2 text-sm text-red-600">{errors.classId.message}</p>
              )}
            </div>

            {selectedClassId && (
              <div>
                <label htmlFor="studentId" className="block text-sm font-medium text-gray-700">
                  교육생 선택
                </label>
                <select
                  id="studentId"
                  {...register('studentId', { required: '교육생을 선택해주세요.' })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">교육생을 선택하세요</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} ({student.studentId})
                    </option>
                  ))}
                </select>
                {errors.studentId && (
                  <p className="mt-2 text-sm text-red-600">{errors.studentId.message}</p>
                )}
              </div>
            )}

            {selectedClassId && (
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  출석 상태
                </label>
                <select
                  id="status"
                  {...register('status', { required: '출석 상태를 선택해주세요.' })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">출석 상태를 선택하세요</option>
                  <option value="present">출석</option>
                  <option value="late">지각</option>
                  <option value="absent">결석</option>
                  <option value="excused">사유 있음</option>
                </select>
                {errors.status && (
                  <p className="mt-2 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            )}

            {selectedClassId && (
              <div>
                <label htmlFor="note" className="block text-sm font-medium text-gray-700">
                  메모 (선택사항)
                </label>
                <textarea
                  id="note"
                  {...register('note')}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="비고사항을 입력하세요"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {successMessage}
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !selectedClassId}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                  isSubmitting || !selectedClassId
                    ? 'bg-indigo-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {isSubmitting ? '처리 중...' : '출석 등록'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 강의 정보 표시 */}
      {selectedClass && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              강의 정보
            </h3>
            <div className="mt-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">강의명</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClass.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">담당 강사</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClass.instructorName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">강의 일정</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClass.schedule}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">강의실</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClass.room}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRegister; 